import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

// SERVICES
import DeliveryRadiusService from "../../api/deliveryRadius";
import MerchantsService from "../../api/merchant";

// APP COMPONENTS
import CreateOrEditDeliveryRadiusModal from "../deliveryRadius/createOrEditDeliveryRadiusModal";
import RemoveModal from "../modals/removeModal";
import FiscalDataForm from "../fiscal/FiscalDataForm";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PaymentIcon from "@mui/icons-material/Payment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// FOOD TYPE ICONS
import RestaurantIcon from "@mui/icons-material/Restaurant";

// STYLED COMPONENTS
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from "../styled";
import { green } from "@mui/material/colors";
import { formatFinance, formatKm } from "../../utils/formatters";
import { getIconColor, getIconComponent } from "../../utils/merchant";
import { extractApiErrorMessage } from "../../utils/subscriptionErrors";
import { FOOD_TYPE_ICONS } from "../../types/merchant";

// Animation variants
const pageVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const cardVariants = {
  initial: { y: 20, opacity: 0 },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

export default function MerchantCreateOrEdit() {
  const theme = useTheme();
  const { id } = useParams();
  const history = useHistory();
  const auth = useSelector((state) => state.auth);

  // STATE
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [label, setLabel] = useState(id ? "Editar Loja" : "Nova Loja");
  const [expanded, setExpanded] = useState(false);

  // MERCHANT FIELDS
  const [name, setName] = useState("");
  const [ifoodId, setIfoodId] = useState("");
  const [neemoToken, setNeemoToken] = useState("");
  const [cardapiowebId, setCardapiowebId] = useState("");
  const [cardapiowebApiKey, setCardapiowebApiKey] = useState("");
  const [hasCardapiowebApiKey, setHasCardapiowebApiKey] = useState(false);
  const [fiscalId, setFiscalId] = useState(null);
  const [foodTypeIcon, setFoodTypeIcon] = useState("RestaurantIcon");

  // DELIVERY RADIUS
  const [deliveryRadius, setDeliveryRadius] = useState([]);
  const [editingDeliveryRadius, setEditingDeliveryRadius] = useState(null);
  const [showDeliveryRadiusModal, setDeliveryRadiusModal] = useState(false);
  const [deliveryRadiusToDelete, setDeliveryRadiusToDelete] = useState(null);
  const [showDeliveryRadiusDeleteModal, setShowDeliveryRadiusDeleteModal] =
    useState(false);

  // ERROR HANDLING
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { rid: id };
      const {
        data: {
          name,
          ifood_id,
          neemo_token,
          cardapioweb_id,
          has_cardapioweb_api_key,
          fk_fiscal_info,
          food_type_icon,
        },
      } = await MerchantsService.getMerchants(params);

      setName(name);
      setIfoodId(ifood_id || "");
      setNeemoToken(neemo_token || "");
      setCardapiowebId(cardapioweb_id || "");
      setHasCardapiowebApiKey(Boolean(has_cardapioweb_api_key));
      setCardapiowebApiKey("");
      setFiscalId(fk_fiscal_info);
      setFoodTypeIcon(food_type_icon || "RestaurantIcon");
      setLabel(`Editar Loja: ${name}`);

      await loadDeliveryRadius();
    } catch (err) {
      setErrorMessage(
        "Não foi possível carregar os dados da loja. Tente novamente."
      );
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryRadius = async () => {
    try {
      const res = await DeliveryRadiusService.getDeliveryRadius({ mid: id });
      setDeliveryRadius(res.data);
    } catch (err) {
      console.error("Error loading delivery radius:", err);
    }
  };

  const createOrEditRestaurant = async () => {
    try {
      setSubmitting(true);
      setErrorMessage("");
      setOpenErrorAlert(false);

      // Validate required fields
      if (!name.trim()) {
        setErrorMessage("O nome da loja é obrigatório");
        setOpenErrorAlert(true);
        setSubmitting(false);
        return;
      }

      const merchant = {
        name: name.trim(),
        ifood_id: ifoodId.trim(),
        neemo_token: neemoToken.trim(),
        cardapioweb_id: cardapiowebId.trim(),
        food_type_icon: foodTypeIcon,
      };

      // Only send the API key when the user typed a new value — empty
      // string keeps the existing encrypted value on the backend.
      if (cardapiowebApiKey.trim()) {
        merchant.cardapioweb_api_key = cardapiowebApiKey.trim();
      }

      if (id) {
        await MerchantsService.editMerchant(merchant, id);
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } else {
        const response = await MerchantsService.createMerchant(merchant);
        // Navigate to edit page with the new merchant id
        history.replace(`/restaurant/${response.data.id}`);
      }
    } catch (err) {
      const errorMsg = extractApiErrorMessage(
        err,
        "Algo deu errado, tente novamente em 1 minuto ou entre em contato com o suporte"
      );
      setErrorMessage(errorMsg);
      setOpenErrorAlert(true);
    } finally {
      setSubmitting(false);
    }
  };

  const createDeliveryRadius = async (deliveryRadiusObj) => {
    try {
      await DeliveryRadiusService.createDeliveryRadius({
        ...deliveryRadiusObj,
        fk_merchant: id,
      });

      handleDeliveryRadiusModal();
      await loadDeliveryRadius();
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }

      setErrorMessage(messages || "Erro ao criar raio de entrega");
      setOpenErrorAlert(true);
    }
  };

  const editDeliveryRadius = async (deliveryRadiusObj) => {
    try {
      await DeliveryRadiusService.editDeliveryRadius(
        deliveryRadiusObj,
        deliveryRadiusObj.id
      );

      handleDeliveryRadiusModal();
      setEditingDeliveryRadius(null);
      await loadDeliveryRadius();
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }

      setErrorMessage(messages || "Erro ao editar raio de entrega");
      setOpenErrorAlert(true);
    }
  };

  const handleExpandedChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleDeliveryRadiusModal = () => {
    setDeliveryRadiusModal(!showDeliveryRadiusModal);
    setEditingDeliveryRadius(null);
  };

  const handleEditDeliveryRadius = (deliveryRadius) => {
    setEditingDeliveryRadius(deliveryRadius);
    setDeliveryRadiusModal(!showDeliveryRadiusModal);
  };

  const handleDeleteDeliveryRadiusModal = (deliveryRadius) => {
    setDeliveryRadiusToDelete(deliveryRadius);
    setShowDeliveryRadiusDeleteModal(!showDeliveryRadiusDeleteModal);
  };

  const handleDeleteDeliveryRadius = async () => {
    try {
      await DeliveryRadiusService.deleteDeliveryRadius(
        deliveryRadiusToDelete.id
      );
      setShowDeliveryRadiusDeleteModal(false);
      setDeliveryRadiusToDelete(null);
      await loadDeliveryRadius();
    } catch (err) {
      setErrorMessage("Erro ao excluir raio de entrega");
      setOpenErrorAlert(true);
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          bgcolor: alpha(theme.palette.background.default, 0.8),
        }}
      >
        <Stack
          spacing={2}
          sx={{
            m: "auto",
            textAlign: "center",
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            Carregando informações...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Get the current selected icon component
  const SelectedIconComponent = getIconComponent(foodTypeIcon);
  const selectedIconColor = getIconColor(foodTypeIcon);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          maxWidth: "1600px",
          mx: "auto",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <motion.div variants={cardVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: "#fff",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <motion.div
                  whileHover={{ rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: selectedIconColor,
                      width: 54,
                      height: 54,
                    }}
                  >
                    <SelectedIconComponent
                      sx={{ fontSize: 32, color: "#ffffff" }}
                    />
                  </Avatar>
                </motion.div>
                <Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {label}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {id
                      ? "Edite as informações da sua loja"
                      : "Preencha as informações para cadastrar sua loja"}
                  </Typography>
                </Box>
              </Box>

              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleGoBack}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                  }}
                >
                  Voltar
                </Button>
              </motion.div>
            </Paper>
          </motion.div>
        </Box>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={5}>
            <motion.div variants={cardVariants}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: "#fff",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  height: "100%",
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Informações Básicas
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Dados principais da sua loja
                  </Typography>
                  <Divider sx={{ mt: 1 }} />
                </Box>

                <Stack spacing={3}>
                  <TextField
                    id="merchant-name"
                    label="Nome da Loja"
                    variant="outlined"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    helperText="Nome que será exibido para os clientes"
                    InputProps={{
                      startAdornment: (
                        <StorefrontIcon color="action" sx={{ mr: 1 }} />
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Food Type Icon Selector */}
                  <FormControl fullWidth>
                    <InputLabel id="food-type-icon-label">
                      Tipo de Estabelecimento
                    </InputLabel>
                    <Select
                      labelId="food-type-icon-label"
                      id="food-type-icon-select"
                      value={foodTypeIcon}
                      label="Tipo de Estabelecimento"
                      onChange={(e) => setFoodTypeIcon(e.target.value)}
                      sx={{
                        borderRadius: 2,
                      }}
                      renderValue={(selected) => {
                        const iconConfig = FOOD_TYPE_ICONS.find(
                          (icon) => icon.id === selected
                        );
                        const IconComponent = iconConfig
                          ? iconConfig.icon
                          : RestaurantIcon;
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconComponent sx={{ color: iconConfig?.color }} />
                            <Typography>
                              {iconConfig?.label || "Restaurante Geral"}
                            </Typography>
                          </Box>
                        );
                      }}
                    >
                      {/* Group icons by category */}
                      {Object.entries(
                        FOOD_TYPE_ICONS.reduce((groups, icon) => {
                          const category = icon.category || "Outros";
                          if (!groups[category]) groups[category] = [];
                          groups[category].push(icon);
                          return groups;
                        }, {})
                      )
                        .map(([category, icons]) => [
                          <ListSubheader
                            key={category}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              fontWeight: "bold",
                              color: theme.palette.primary.main,
                            }}
                          >
                            {category}
                          </ListSubheader>,
                          ...icons.map((iconConfig) => {
                            const IconComponent = iconConfig.icon;
                            return (
                              <MenuItem
                                key={iconConfig.id}
                                value={iconConfig.id}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    width: "100%",
                                  }}
                                >
                                  <IconComponent
                                    sx={{ color: iconConfig.color }}
                                  />
                                  <Typography>{iconConfig.label}</Typography>
                                </Box>
                              </MenuItem>
                            );
                          }),
                        ])
                        .flat()}
                    </Select>
                    <FormHelperText>
                      Escolha o ícone que melhor representa seu tipo de
                      estabelecimento ou comida
                    </FormHelperText>
                  </FormControl>

                  <TextField
                    id="ifood-id"
                    label="ID da loja iFood"
                    variant="outlined"
                    fullWidth
                    autoComplete="off"
                    value={ifoodId}
                    onChange={(e) => setIfoodId(e.target.value)}
                    helperText="Disponível no Portal iFood > Configurações > Perfil da Loja > ID da loja"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <TextField
                    id="neemo-token"
                    label="Neemo Token"
                    variant="outlined"
                    fullWidth
                    autoComplete="off"
                    value={neemoToken}
                    onChange={(e) => setNeemoToken(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <TextField
                    id="cardapioweb-id"
                    label="ID da loja CardápioWeb"
                    variant="outlined"
                    fullWidth
                    autoComplete="off"
                    value={cardapiowebId}
                    onChange={(e) => setCardapiowebId(e.target.value)}
                    helperText="ID do merchant no CardápioWeb"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <TextField
                    id="cardapioweb-api-key"
                    label="API Key CardápioWeb"
                    type="password"
                    variant="outlined"
                    fullWidth
                    value={cardapiowebApiKey}
                    onChange={(e) => setCardapiowebApiKey(e.target.value)}
                    placeholder={
                      hasCardapiowebApiKey ? "••••••••" : ""
                    }
                    helperText={
                      hasCardapiowebApiKey
                        ? "Uma chave já está cadastrada. Preencha apenas para substituí-la."
                        : "Token gerado em CONFIGURAÇÕES → INTEGRAÇÕES → API DE INTEGRAÇÃO"
                    }
                    autoComplete="new-password"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <Box sx={{ mt: 2 }}>
                    <motion.div
                      whileHover="hover"
                      whileTap="tap"
                      variants={buttonVariants}
                    >
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={submitting}
                        onClick={createOrEditRestaurant}
                        startIcon={success ? <CheckCircleIcon /> : <SaveIcon />}
                        sx={{
                          borderRadius: 2,
                          py: 1.2,
                          background: success ? green[600] : undefined,
                          position: "relative",
                        }}
                      >
                        {submitting
                          ? "Processando..."
                          : success
                          ? "Salvo com Sucesso!"
                          : id
                          ? "Salvar Alterações"
                          : "Cadastrar Loja"}
                        {submitting && (
                          <CircularProgress
                            size={24}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              marginTop: "-12px",
                              marginLeft: "-12px",
                            }}
                          />
                        )}
                      </Button>
                    </motion.div>
                  </Box>

                  {!id && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.dark,
                        border: `1px solid ${alpha(
                          theme.palette.info.main,
                          0.2
                        )}`,
                      }}
                    >
                      Após enviar seu cadastro fique atento a sua caixa de
                      e-mails, em até 1h, dentro do horário de atendimento,
                      enviaremos um e-mail com os próximos passos.
                    </Typography>
                  )}

                  <Collapse in={openErrorAlert}>
                    <Alert
                      variant="filled"
                      severity="error"
                      icon={<ErrorOutlineIcon />}
                      action={
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          size="small"
                          onClick={() => {
                            setOpenErrorAlert(false);
                          }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      {errorMessage}
                    </Alert>
                  </Collapse>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>

          {/* Advanced Settings */}
          <Grid item xs={12} md={7}>
            <motion.div variants={cardVariants}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: "#fff",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Configurações Avançadas
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Configure raios de entrega e dados fiscais
                  </Typography>
                  <Divider sx={{ mt: 1 }} />
                </Box>

                {/* Delivery Radius Panel */}
                <StyledAccordion
                  expanded={expanded === "deliveryRadiusPanel"}
                  onChange={handleExpandedChange("deliveryRadiusPanel")}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    borderRadius: "12px !important",
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{
                      borderRadius: "12px !important",
                      "&.Mui-expanded": {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <LocalShippingIcon color="primary" />
                      <Typography sx={{ fontWeight: 700 }}>
                        Raios de Entrega
                      </Typography>
                    </Box>
                    <Chip
                      label={`${deliveryRadius.length} ${
                        deliveryRadius.length === 1 ? "raio" : "raios"
                      }`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 2 }}
                    />
                  </StyledAccordionSummary>
                  <StyledAccordionDetails sx={{ p: 0 }}>
                    <Divider />
                    <Box sx={{ p: 3 }}>
                      <AnimatePresence>
                        {id && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Button
                              variant="contained"
                              startIcon={<AddCircleIcon />}
                              onClick={handleDeliveryRadiusModal}
                              sx={{ mb: 3, borderRadius: 2 }}
                              color="primary"
                            >
                              Novo Raio de Entrega
                            </Button>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {!id && (
                        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                          Para configurar os raios de entrega, primeiro salve a
                          loja.
                        </Alert>
                      )}

                      {deliveryRadius.length > 0 ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2
                            )}`,
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            }}
                          >
                            <Grid container spacing={1}>
                              <Grid item xs={3}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  De (km)
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  Até (km)
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  Valor
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  align="center"
                                >
                                  Ações
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>

                          <Divider />

                          <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                            {deliveryRadius.map((radius, index) => (
                              <React.Fragment key={radius.id || index}>
                                {index > 0 && <Divider />}
                                <Box
                                  sx={{
                                    p: 2,
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.02
                                      ),
                                    },
                                  }}
                                >
                                  <Grid
                                    container
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Grid item xs={3}>
                                      <Typography variant="body2">
                                        {formatKm(radius.from_distance)} km
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                      <Typography variant="body2">
                                        {formatKm(radius.to_distance)} km
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                      <Chip
                                        label={formatFinance(
                                          parseFloat(radius.delivery_tax)
                                        )}
                                        size="small"
                                        color="success"
                                        sx={{ fontWeight: "bold" }}
                                      />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Tooltip title="Editar">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleEditDeliveryRadius(radius)
                                            }
                                            sx={{
                                              color: theme.palette.primary.main,
                                              "&:hover": {
                                                bgcolor: alpha(
                                                  theme.palette.primary.main,
                                                  0.1
                                                ),
                                              },
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDeleteDeliveryRadiusModal(
                                                radius
                                              )
                                            }
                                            sx={{
                                              color: theme.palette.error.main,
                                              "&:hover": {
                                                bgcolor: alpha(
                                                  theme.palette.error.main,
                                                  0.1
                                                ),
                                              },
                                            }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </React.Fragment>
                            ))}
                          </Box>
                        </Paper>
                      ) : (
                        <Box
                          sx={{
                            p: 4,
                            textAlign: "center",
                            border: `1px dashed ${alpha(
                              theme.palette.divider,
                              0.5
                            )}`,
                            borderRadius: 2,
                          }}
                        >
                          <LocalShippingIcon
                            sx={{
                              fontSize: 48,
                              color: alpha(theme.palette.text.secondary, 0.3),
                              mb: 2,
                            }}
                          />
                          <Typography color="textSecondary" gutterBottom>
                            Nenhum raio de entrega configurado
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mb: 3 }}
                          >
                            Adicione raios de entrega para definir taxas
                            baseadas na distância
                          </Typography>

                          {id && (
                            <Button
                              variant="outlined"
                              startIcon={<AddCircleIcon />}
                              onClick={handleDeliveryRadiusModal}
                              size="small"
                            >
                              Adicionar Raio
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </StyledAccordionDetails>
                </StyledAccordion>

                {/* Fiscal Module */}
                <StyledAccordion
                  expanded={expanded === "fiscalPanel"}
                  onChange={handleExpandedChange("fiscalPanel")}
                  variant="outlined"
                  sx={{
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    borderRadius: "12px !important",
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                    sx={{
                      borderRadius: "12px !important",
                      "&.Mui-expanded": {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <PaymentIcon color="primary" />
                      <Typography sx={{ fontWeight: 700 }}>
                        Módulo Fiscal
                      </Typography>
                    </Box>
                    <Chip
                      label={fiscalId ? "Preenchido" : "Não preenchido"}
                      size="small"
                      color={fiscalId ? "success" : "error"}
                      sx={{ ml: 2 }}
                    />
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    <Divider sx={{ mb: 3 }} />
                    {expanded === "fiscalPanel" && id ? (
                      <FiscalDataForm fiscalId={fiscalId} isMerchant />
                    ) : !id ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Para preencher os dados fiscais, primeiro salve a loja.
                      </Alert>
                    ) : null}
                  </StyledAccordionDetails>
                </StyledAccordion>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Box>

      {/* Modals */}
      <RemoveModal
        title="Excluir Raio de Entrega"
        message={`Deseja excluir o raio de entrega de ${formatKm(
          deliveryRadiusToDelete?.from_distance
        )}km até ${formatKm(deliveryRadiusToDelete?.to_distance)}km?`}
        show={showDeliveryRadiusDeleteModal}
        toggleModal={handleDeleteDeliveryRadiusModal}
        onRemove={handleDeleteDeliveryRadius}
      />

      {showDeliveryRadiusModal && (
        <CreateOrEditDeliveryRadiusModal
          isMerchant
          show={showDeliveryRadiusModal}
          closeModal={handleDeliveryRadiusModal}
          createDeliveryRadius={createDeliveryRadius}
          editDeliveryRadius={editDeliveryRadius}
          deliveryRadius={editingDeliveryRadius}
        />
      )}
    </motion.div>
  );
}

// Helper component
const Avatar = ({ sx, children }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
